/**
 * The single place display formatting of numeric values is defined. Data table cells, metric cards
 * and chart axes/tooltips all go through here so the same value reads identically wherever it lands.
 */

/**
 * Presentation locale for all numeric output. Pinned rather than following the interface language:
 * clients are NL/DE, and a shared report should read the same for everyone discussing it.
 * If this becomes a user- or client-level setting, change only resolveNumberLocale().
 */
const DEFAULT_NUMBER_LOCALE = "nl-NL";

/** The only source of the number locale — no formatter may pass a literal one to Intl. */
export function resolveNumberLocale(): string {
  return DEFAULT_NUMBER_LOCALE;
}

/**
 * How a value is rendered. Each member names a *presentation*, not what the value means — so a
 * total, a denominator and a chart tick are all `whole` if they should read without decimals.
 *
 * - `whole` — grouped, no decimals: `1234567` → `1.234.567`. Visits, clicks, record totals.
 *   Maps from the backend's `Int` column type.
 * - `compact` — short form for tight spaces, at most 1 decimal: `18500` → `18,5K`. Pair it with
 *   `whole` in an aria-label or tooltip.
 * - `decimal` — grouped, always 2 decimals: `3.4` → `3,40`. Ratios and averages, e.g. interactions
 *   per visit. Maps from `Float`.
 * - `currency` — grouped, always 2 decimals, with the currency symbol: `5` → `€ 5,00`. Always 2dp
 *   even for whole amounts, so a card and the cell beside it can't disagree.
 * - `percentage` — grouped, 2 decimals, `%` suffix: `6.67` → `6,67%`. The value must already be
 *   scaled (6.67 meaning 6.67%); this never multiplies by 100.
 * - `text` — passed through via `String()`. For non-numeric payloads that still need the shared
 *   absent-value handling.
 *
 * Absent (`null`/`undefined`/`NaN`) renders as the `na` sentinel for every member. A real `0` is a
 * value and always renders as a number.
 */
export type ValueFormat = "whole" | "compact" | "decimal" | "currency" | "percentage" | "text";

/** Fallback shown when a value is absent; callers override via opts.na (usually t("common.na")). */
const DEFAULT_NA = "N/A";

const CURRENCY = "EUR";
const TWO_DP = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

type FormatValueOptions = {
  /**
   * Required, not defaulted: a forgotten format would silently render a currency or rate as a plain
   * number — the card/cell disagreement this module exists to prevent.
   */
  format: ValueFormat;
  /** Rendered when the value is absent (null/undefined/NaN). A real 0 is a value, not absence. */
  na?: string;
};

/**
 * Absence, as distinct from zero. Zero is a measured result and always renders as a number; hiding
 * it is a per-column display choice, expressed via `showZeroAs` on the column definition — never
 * inferred here, or "no data" and "measured zero" become indistinguishable.
 */
function isAbsent(value: number | null | undefined): boolean {
  return value == null || Number.isNaN(value);
}

/** Grouped, no decimals — see the `whole` member of {@link ValueFormat}. */
export function formatWhole(value: number | null | undefined, na = DEFAULT_NA): string {
  if (isAbsent(value)) return na;
  return new Intl.NumberFormat(resolveNumberLocale()).format(value!);
}

/** Short form for tight spaces — see the `compact` member of {@link ValueFormat}. */
function formatCompactWhole(value: number | null | undefined, na = DEFAULT_NA): string {
  if (isAbsent(value)) return na;
  return new Intl.NumberFormat(resolveNumberLocale(), { notation: "compact", maximumFractionDigits: 1 }).format(value!);
}

/** A 2-decimal ratio (e.g. avg interactions per visit). */
function formatDecimalMetric(value: number | null | undefined, na: string): string {
  if (isAbsent(value)) return na;
  return new Intl.NumberFormat(resolveNumberLocale(), TWO_DP).format(value!);
}

/**
 * A rate the backend already expressed as a percentage (6.67 → "6,67%").
 * Not `style: "percent"` — the value is already scaled, so that would multiply it by 100.
 */
function formatPercentage(value: number | null | undefined, na: string): string {
  if (isAbsent(value)) return na;
  return `${new Intl.NumberFormat(resolveNumberLocale(), TWO_DP).format(value!)}%`;
}

/** Money, always 2 decimals — one contract, so a card and the cell beside it can't disagree. */
export function formatCurrency(value: number | null | undefined, na = DEFAULT_NA): string {
  if (isAbsent(value)) return na;
  return new Intl.NumberFormat(resolveNumberLocale(), {
    style: "currency",
    currency: CURRENCY,
    ...TWO_DP,
  }).format(value!);
}

/**
 * Formats a value for display per {@link ValueFormat}. An absent value renders as the `na`
 * sentinel for every format — callers that would rather show "0" must pass that value explicitly.
 *
 * Prefer the `useValueFormatter` hook inside components: it supplies the translated `na` label.
 */
export function formatValue(
  value: number | null | undefined,
  { format, na = DEFAULT_NA }: FormatValueOptions,
): string {
  switch (format) {
  case "whole":
    return formatWhole(value, na);
  case "compact":
    return formatCompactWhole(value, na);
  case "currency":
    return formatCurrency(value, na);
  case "percentage":
    return formatPercentage(value, na);
  case "text":
    return isAbsent(value) ? na : String(value);
  case "decimal":
  default:
    return formatDecimalMetric(value, na);
  }
}
